import { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import AppLayout from "@/components/feature/AppLayout";
import { useTickets } from "@/pages/tickets/hooks/useTickets";
import { useInboxConversations } from "@/pages/chat/hooks/useInboxConversations";
import { useConversationThread } from "@/pages/chat/hooks/useConversationThread";
import { useInboxRealtime } from "@/pages/chat/hooks/useInboxRealtime";
import { useTicketThreadMessages } from "@/pages/tickets/hooks/useTicketThreadMessages";
import TicketingResizableShell from "./components/TicketingResizableShell";
import TicketingConversationList from "./components/TicketingConversationList";
import TicketingChatThread from "./components/TicketingChatThread";
import TicketingDetailsPanel from "./components/TicketingDetailsPanel";
import "@/pages/tickets/styles/index.css";

export default function TicketingPage() {
  const { activeWorkspaceId } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [listExpandStatusId, setListExpandStatusId] = useState(null);
  const [activeCategory] = useState("all");
  const ticketCreatedEvent = null;

  const {
    tickets,
    ticketQueueSections,
    loading: ticketsLoading,
    submitTicketWithStatus,
    setTicketGroup,
    refreshTickets,
  } = useTickets(activeWorkspaceId);

  const {
    conversations,
    error: listError,
    refresh: refreshInbox,
  } = useInboxConversations(activeCategory);

  const selectedTicket = useMemo(() => {
    if (!selectedTicketId) return null;
    return tickets.find((t) => t.id === selectedTicketId) ?? null;
  }, [tickets, selectedTicketId]);

  // Only fetch a live-chat conversation when the ticket actually has one. Email-origin
  // tickets have no chat session, so fetching one would 404 (conversation_not_found);
  // they render from their ticket messages instead.
  const conversationFetchId = useMemo(
    () => (selectedTicket?.hasConversation ? selectedTicket.conversationId : null),
    [selectedTicket],
  );

  const thread = useConversationThread(conversationFetchId, ticketCreatedEvent, {
    workspaceId: activeWorkspaceId,
  });

  const {
    conversation,
    loading: threadLoading,
    error: threadError,
    sendMessage,
    reload: reloadThread,
    applyThreadData,
    appendMessage,
  } = thread;

  const { mergedMessages, refreshTicketMessages } = useTicketThreadMessages({
    selectedTicket,
    inboxMessages: thread.messages,
    workspaceId: activeWorkspaceId,
  });

  const handleAfterTicketAction = useCallback(() => {
    refreshTicketMessages();
    void reloadThread({ silent: true });
  }, [refreshTicketMessages, reloadThread]);

  useEffect(() => {
    const st = location.state;
    if (!st?.focusTicketId || !st?.conversationId) return;
    setSelectedTicketId(String(st.focusTicketId));
    setSelectedId(String(st.conversationId));
    const rawStatus = st.ticketStatus;
    if (rawStatus && typeof rawStatus === "string") {
      setListExpandStatusId(String(rawStatus).trim().toLowerCase());
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (selectedTicketId) return;
    const first = tickets[0];
    if (!first) return;
    setSelectedTicketId(first.id);
    setSelectedId(first.conversationId);
  }, [tickets, selectedTicketId]);

  const handleSelectTicket = useCallback((ticket) => {
    setSelectedId(ticket.conversationId);
    setSelectedTicketId(ticket.id);
  }, []);

  const handleRetryQueues = useCallback(() => {
    void refreshInbox({ silent: false });
    void refreshTickets({ silent: false });
  }, [refreshInbox, refreshTickets]);

  const handleRealtimeMessage = useCallback(
    (msg) => {
      if (!msg) return;
      appendMessage(msg);
    },
    [appendMessage],
  );

  const handleThreadSync = useCallback(
    (data) => {
      applyThreadData(data);
    },
    [applyThreadData],
  );

  useInboxRealtime(
    isHumanAgentInboxLive()
      ? {
          conversationId: conversationFetchId,
          workspaceId: activeWorkspaceId,
          onMessage: handleRealtimeMessage,
          onThreadSync: handleThreadSync,
        }
      : {
          conversationId: null,
          workspaceId: null,
          onMessage: handleRealtimeMessage,
        },
  );

  return (
    <AppLayout>
      <div className="ticketing-page-canvas">
        <TicketingResizableShell
          selectedId={selectedTicketId}
          list={
            <TicketingConversationList
              selectedTicketId={selectedTicketId}
              onSelectTicket={handleSelectTicket}
              panelTitle="Tickets"
              panelSubtitle="Ticketing queue"
              tickets={tickets}
              ticketQueueSections={ticketQueueSections}
              ticketsLoading={ticketsLoading}
              conversations={conversations}
              listError={listError}
              onRetryList={handleRetryQueues}
              expandStatusSectionId={listExpandStatusId}
            />
          }
          thread={
            <TicketingChatThread
              conversationId={selectedId}
              conversation={conversation}
              messages={mergedMessages}
              threadLoading={threadLoading}
              threadError={threadError}
              sendMessage={sendMessage}
              onRetryThread={reloadThread}
              selectedTicket={selectedTicket}
              submitTicketWithStatus={submitTicketWithStatus}
              onAfterTicketAction={handleAfterTicketAction}
              workspaceId={activeWorkspaceId}
            />
          }
          details={
            <TicketingDetailsPanel
              conversation={conversation}
              conversationLoading={threadLoading}
              workspaceId={activeWorkspaceId}
              selectedTicket={selectedTicket}
              onChangeTicketGroup={setTicketGroup}
            />
          }
        />
      </div>
    </AppLayout>
  );
}
